import { ChevronLeft, Clock4 } from "lucide-react";
import { Server } from "@wajeez/wajeez.microservices";
import { Card, CardContent } from "@ui/card";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import { useIsLoadingVisibile } from "@utils/hooks/useIsLoadingVisible";
import { Skeleton } from "@ui/skeleton";
import { OnItemPress } from "@modules/content/hooks/useComponentData";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import {
  convertMsToHourMinSec,
  formatDurationInMinutes,
} from "@utils/helpers/timeFormtter";
import { useLanguage } from "@modules/localizations/LanguageContext";

export interface FeaturedContentV3Parameters {
  image: string | null | undefined;
  buttonText: string | null | undefined;
  title: string | null | undefined;
  subTitle: string | null | undefined;
  categories: string | null | undefined;
  description: string | null | undefined;
}

interface FeaturedContentV3Props {
  parameterValues: Partial<FeaturedContentV3Parameters>;
  content:
    | (Server.ContentEntityOutput & { isLocked: boolean | undefined })
    | undefined
    | null;
  isLoading: boolean;
  onItemPress: OnItemPress<Partial<FeaturedContentV3Parameters>>;
}

const FeaturedContentV3 = ({
  parameterValues,
  content,
  isLoading,
  onItemPress,
}: FeaturedContentV3Props) => {
  const { language } = useLanguage();
  const { image, buttonText, title, categories, description } = parameterValues;
  const isLoadingVisible = useIsLoadingVisibile(isLoading && !content?.id);

  return (
    <div
      className={`relative w-full ${
        (isLoadingVisible || isLoading) && "h-[350px]"
      } px-10 pb-[35px] text-ramadan-primary dark:text-white`}
    >
      <div
        className={`absolute top-0 h-full w-full px-10 ltr:left-0 rtl:right-0 ${
          isLoadingVisible && "z-10 bg-white dark:bg-black"
        }`}
      >
        <div className="h-full pb-[35px]">
          <FeaturedContentV3Loading isLoading={isLoadingVisible} />
        </div>
      </div>
      {content?.id ? (
        <>
          <div className="flex flex-col gap-3 pb-5">
            <div className="text-[24px] font-bold">{title}</div>
          </div>
          <Card
            className="w-full cursor-pointer border-none bg-[#F1F5F6] p-5 dark:bg-[#102133]"
            onClick={() => {
              content &&
                onItemPress(content, parameterValues, {
                  isLocked: content.isLocked,
                });
            }}
          >
            <CardContent className="flex gap-5 p-0">
              <img
                src={getPublicMediaUrl(image)}
                alt="daily-content"
                width={235}
                height={235}
                className="rounded-[10px]"
              />
              <div className="flex flex-col items-start gap-2 dark:text-white">
                <div className="flex items-center justify-center gap-2 text-[20px] font-medium text-primary dark:text-[#64A6E1]">
                  <Clock4 />
                  <div>
                    {content.contentEntityType === "BOOK"
                      ? formatDurationInMinutes(
                          content.durationMS || 0,
                          language
                        )
                      : convertMsToHourMinSec(content.durationMS || 0)}
                  </div>
                </div>
                <div className="text-[20px] font-bold">{content.title}</div>
                <div className="text-[20px]">{content.authors?.join(", ")}</div>
                <div className="text-[20px] text-[#A5A5A5]">{categories}</div>
                <div className="mb-10 text-[18px] dark:text-white">
                  {description}
                </div>
              </div>
              <div className="absolute bottom-12 end-14">
                <ShadcnButton className="flex items-start justify-center rounded-lg pb-1 pe-1 ps-5 pt-1 text-[20px] font-normal text-white hover:opacity-80 dark:bg-[#64A6E1] dark:text-[#010D19] dark:hover:bg-[#64A6E1]">
                  <div>{buttonText}</div>
                  <ChevronLeft size={30} className="my-auto" />
                </ShadcnButton>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
};

const FeaturedContentV3Loading = ({ isLoading }: { isLoading: boolean }) => {
  return isLoading ? (
    <div className="mx-auto flex w-full flex-col justify-center gap-3 pb-5">
      <Skeleton className="h-5 w-20" />
      <Skeleton className="h-5 w-[200px]" />
      <Skeleton className={`mt-7 h-[250px] w-full rounded-[12px]`} />
    </div>
  ) : null;
};

export default FeaturedContentV3;
